/* eslint-disable no-plusplus */
import React, {
  // useRef,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from 'react';
import classnames from 'classnames';
import { useRequest } from 'ahooks';
import { Segment, Table, TableProps } from 'tea-component';
import CardSlot from '@gp/components/CardSlot';
import TriggerViewCard from '@gp/components/TriggerViewCard';
// import GeneralDownloadBtn from '@gp/components/GeneralDownloadBtn';
import BulletChart from '@gp/components/BulletChart';
import GeneralLegend from '@gp/components/GeneralLegend';
// import PercentProgress from './components/PercentProgress';
import { reportOperationLog } from '@gp/services/global';
import {
  fetchStudioOverallFinanceOverviewData,
  studioOverallFinanceOverviewDownLoad,
} from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverall';
import {
  getFormatNumPlus,
  getDownLoadCSVResponseHeaderFileName,
  downloadFileByBlob,
  stream2Object,
} from '@gp/utils/common';
import DataTypeUtils from '@gp/utils/DataTypeUtils';
import { getTableList, getColumns } from './utils';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';
import {
  // FILE_TYPES2,
  FILE_TYPES_MAP,
} from '@gp/constants/const';
import {
  SEGMENT_OPT,
  SEGMENT_GROSS_REVENUE,
  LEGEND_OPT,
  RECORDS_MAP,
  DOWNLOAD_LOG_MAP,
  COMMON_DEFAULT_VALUE,
} from './constants';

import './index.scss';

const { autotip, injectable, mergeable, scrollable } = Table.addons;

interface OverviewPropsType {
  selectBO: string[];
  filterBOLoading: boolean;
}

type CommonType = {
  grossRevenue: any;
  netProfit: any;
};

type IRecords = {
  uniqueId: string;
  boName: string;
  isSpacerBlock: boolean;
  isAllTotal: boolean;
  isTotal: boolean;
  studioId: string;
  studioName: string;
  studioCover: string;
  ytm: string;
  nearestYearBudget: string;
  completion: string;
  nearestYearForecast: string;
  nearestYearActual: string;
};

interface ICardType extends IRecords {
  title: string;
  budget_date: string;
  forecast_date: string;
  pre_year: string;
  ytm_date: string;
}

const DEFAULT_SELECT_BO: string[] = [];

const Overview: React.FC<OverviewPropsType> = (props) => {
  const { selectBO = DEFAULT_SELECT_BO, filterBOLoading = true } = props;

  const [segmentValue, setSegmentValue] = useState<'1' | '2'>(SEGMENT_GROSS_REVENUE);
  // 两个卡片的数据
  const [cardList, setCardList] = useState<CommonType>(COMMON_DEFAULT_VALUE);
  const [toDownload, setToDownload] = useState<any>(null);
  const [records, setRecords] = useState<IRecords[]>([]);
  const [tableVirtualLoading, setTableVirtualLoading] = useState<boolean>(false);
  // 时间相关的数据
  const [dateList, setDateList] = useState<CommonType>(COMMON_DEFAULT_VALUE);
  // 全选时的时间相关数据，表格在没有数据的情况下会展示第一行总的total，此时表头时间也要展示（但后端这是不返回时间）
  const [allTotalDateList, setAllTotalDateList] = useState<CommonType>(COMMON_DEFAULT_VALUE);
  // 列表第一行Portfolio Total的数据
  const [firstAllTotalList, setFirstAllTotalList] = useState<CommonType>(COMMON_DEFAULT_VALUE);
  // 表格是否出现横向滚动条
  // const [tableIsScroll, setTableIsScroll] = useState<boolean>(false);
  // const [scrollTop, setScrollTop] = useState<number>(0);
  // const firstRowContainerRef = useRef<HTMLElement>(null);

  const [data, setData] = useState<any>({});
  const [listLoading, setListLoading] = useState(false);

  // overview页面整体数据接口
  // const { loading: listLoading, data, run } = useRequest(fetchStudioOverallFinanceOverviewData, { manual: true });

  const run = async () => {
    try {
      setListLoading(true);
      const res = await fetchStudioOverallFinanceOverviewData({ management_team: [] });
      setData(res || {});
    } catch (exception) {
      console.error(exception);
    } finally {
      setListLoading(false);
    }
  };

  // 子弹图下载
  const {
    loading: downloadLoading,
    data: downloadData,
    run: runDownload,
  } = useRequest(studioOverallFinanceOverviewDownLoad, { manual: true });

  const loading = useMemo(() => filterBOLoading || listLoading, [filterBOLoading, listLoading]);

  const getBulletChartTabs = useCallback(
    (
      dataLoading: boolean,
      data: ICardType,
      downloadLoading: boolean,
      handleDownload: (payload: any) => void,
      // 水平方向hover的位置超过容器一半时是否浮窗在鼠标的左右位置变化
      isTooltipHalfAdaptPosition?: boolean,
    ) => {
      if (dataLoading) return [];

      return [
        {
          id: 'download',
          // 暂时隐藏子弹图的下载入口
          // label: (
          //   <GeneralDownloadBtn
          //     uiType="2"
          //     fileTypes={FILE_TYPES2}
          //     isLoading={downloadLoading}
          //     onConfirm={handleDownload}
          //   />
          // ),
          label: null,
          component: (
            <div className="dbg-studio-overall-finance-overview-bullet-chart-body">
              <BulletChart
                formatNumber={{
                  func: getFormatNumPlus,
                  params: ['_entranceValue'],
                }}
                option={{
                  bgBar: {
                    value: data?.nearestYearForecast,
                  },
                  mainBar: {
                    value: data?.ytm,
                  },
                  markLine: {
                    value: data?.nearestYearBudget,
                  },
                }}
                tooltipData={data}
                isTooltipHalfAdaptPosition={isTooltipHalfAdaptPosition}
              />
              <GeneralLegend
                className="dbg-studio-overall-finance-overview-bullet-chart-legend"
                optList={LEGEND_OPT}
              />
            </div>
          ),
        },
        // {
        //   id: 'ytm_date',
        //   label: <h3 className="dbg-studio-overall-finance-overview-bullet-chart-date">{data?.ytm_date || '-'}</h3>,
        //   component: null,
        // },
      ];
    },
    [],
  );

  const handleGrossRevenueDownload = useCallback((payload: any) => {
    setToDownload({
      downloadTime: Date?.now(),
      downloadCard: 'revenue', // 与后端的字段值对应
      payload,
    });
  }, []);

  const handleNetProfitDownload = useCallback((payload: any) => {
    setToDownload({
      downloadTime: Date?.now(),
      downloadCard: 'profit', // 与后端的字段值对应
      payload,
    });
  }, []);

  const grossRevenueTabs = useMemo(() => {
    let data: any = {
      title: 'Gross Revenue',
    };
    if (DataTypeUtils.isObject(cardList?.grossRevenue)) {
      data = { ...data, ...cardList?.grossRevenue };
    }
    if (DataTypeUtils.isObject(dateList?.grossRevenue)) {
      data = { ...data, ...dateList?.grossRevenue };
    }
    return getBulletChartTabs(
      listLoading,
      data,
      downloadLoading && toDownload?.downloadCard === 'revenue',
      handleGrossRevenueDownload,
      false,
    );
  }, [
    listLoading,
    cardList?.grossRevenue,
    dateList?.grossRevenue,
    downloadLoading,
    toDownload?.downloadCard,
  ]);

  const netProfitTabs = useMemo(() => {
    let data: any = {
      title: 'Net Profit',
    };
    if (DataTypeUtils.isObject(cardList?.netProfit)) {
      data = { ...data, ...cardList?.netProfit };
    }
    if (DataTypeUtils.isObject(dateList?.netProfit)) {
      data = { ...data, ...dateList?.netProfit };
    }
    return getBulletChartTabs(
      listLoading,
      data,
      downloadLoading && toDownload?.downloadCard === 'profit',
      handleNetProfitDownload,
      true,
    );
  }, [
    listLoading,
    cardList?.netProfit,
    dateList?.netProfit,
    downloadLoading,
    toDownload?.downloadCard,
  ]);

  // 下载
  useEffect(() => {
    if (toDownload?.downloadTime) {
      runDownload({
        management_team: selectBO,
        metrics: toDownload?.downloadCard,
        file_type: toDownload?.payload?.fileType === FILE_TYPES_MAP.CSV ? 1 : 2,
      });
    }
  }, [toDownload?.downloadTime]);

  // 抛出下载后的文件
  const throwDownLoadResult = async () => {
    try {
      // 拿到响应头中的文件名称
      const fileName = getDownLoadCSVResponseHeaderFileName((downloadData as any)?.headers);
      downloadFileByBlob((downloadData as any)?.data, fileName);
      const logKey =
        ALL_REPORT_OPERATION_LOG?.[
          DOWNLOAD_LOG_MAP?.[`${toDownload?.payload?.fileType}_${toDownload?.downloadCard}`]
        ];
      logKey && reportOperationLog(logKey, 'button');
    } catch (err: any) {
      const resData = await stream2Object(err?.data);
      throw new Error(resData?.msg || '');
    }
  };
  // 下载结果
  useEffect(() => {
    if (downloadLoading || !downloadData) return;
    throwDownLoadResult();
  }, [downloadLoading]);

  // 组织数据
  const orgList = useMemo(() => {
    // 组织时间相关数据 start
    const grossRevenueList = data?.gross_revenue;
    const netProfitList = data?.net_profit;
    const grossRevenueMetrics = grossRevenueList?.metrics;
    const netProfitMetrics = netProfitList?.metrics;
    const dateList: any = {
      grossRevenue: null,
      netProfit: null,
    };
    if (grossRevenueList) {
      if (Reflect.has(grossRevenueList, 'metrics')) {
        // @ts-ignore
        delete grossRevenueList.metrics;
      }
      dateList.grossRevenue = grossRevenueList;
    }
    if (netProfitList) {
      if (Reflect.has(netProfitList, 'metrics')) {
        // @ts-ignore
        delete netProfitList.metrics;
      }
      dateList.netProfit = netProfitList;
    }
    setDateList(dateList);
    // bo全选时设置
    !selectBO?.length && setAllTotalDateList(dateList);
    // 组织时间相关数据 end

    const res: any = {
      grossRevenue: [],
      netProfit: [],
    };
    res.grossRevenue = getTableList(grossRevenueMetrics);
    res.netProfit = getTableList(netProfitMetrics);
    if (!selectBO?.length) {
      setFirstAllTotalList({
        grossRevenue: res?.grossRevenue?.[0],
        netProfit: res?.netProfit?.[0],
      });
    }
    setCardList({
      grossRevenue: res?.grossRevenue?.[0],
      netProfit: res?.netProfit?.[0],
    });
    setRecords(res?.[RECORDS_MAP?.[segmentValue]] || []);

    return res;
  }, [listLoading, selectBO]);

  const firstLineList = useMemo(
    () => firstAllTotalList?.[RECORDS_MAP?.[segmentValue]],
    [firstAllTotalList, segmentValue],
  );

  const isNoDataOfGrossRevenue = useMemo(() => {
    const data = orgList?.grossRevenue?.[0];

    return (
      !data?.ytm &&
      !data?.nearestYearForecast &&
      !data?.nearestYearBudget &&
      !data?.nearestYearActual
    );
  }, [orgList?.grossRevenue?.[0]]);

  const isNoDataOfNetProfit = useMemo(() => {
    const data = orgList?.netProfit?.[0];

    return (
      !data?.ytm &&
      !data?.nearestYearForecast &&
      !data?.nearestYearBudget &&
      !data?.nearestYearActual
    );
  }, [orgList?.netProfit?.[0]]);

  // 第一行固定的内容是否显示
  // const isShowFirstRow = useMemo(
  //   () => !loading && !tableVirtualLoading && firstLineList,
  //   [loading, tableVirtualLoading, firstLineList],
  // );

  // 根据数据 计算合并单元格情况
  const [rowGroupNumList, setRowGroupNumList] = useState<Array<number>>([]);
  useEffect(() => {
    if (loading || tableVirtualLoading) return;
    if (Array.isArray(records) && records?.length) {
      const tempList = [];
      let preRowId = 0;
      let rowNum = 0;
      for (let i = 0;i < records?.length;i++) {
        const t = records?.[i];
        if (t?.isTotal) {
          rowNum = i - preRowId;
          preRowId = i;
          tempList.push(rowNum);
        }
      }
      tempList.push(records?.length - preRowId);
      tempList.shift();
      setRowGroupNumList(tempList);
    }
  }, [loading, tableVirtualLoading, records]);

  let groupIndex = 0;

  const addons = [
    scrollable({
      minWidth: 1060,
      // maxHeight: 500,
    }),
    injectable({
      cell: (props, context) => ({
        className: `${
          (context.rowKey === '0' && context.columnIndex !== 0) ||
          (context.columnIndex === 0 && context.recordIndex === 0)
            ? 'table-highlight-cell'
            : ''
        }`,
      }),
      row: (props, context) => ({
        className: context.rowKey === '0' && context.recordIndex !== 0 ? 'table-group-row' : '',
      }),
    }),
    mergeable({
      rowSpan: (columnIndex, recordIndex, context) => {
        if (columnIndex === 0) {
          if (context.rowKey === '0' && groupIndex < rowGroupNumList.length) {
            groupIndex++;
            return rowGroupNumList[groupIndex - 1];
          }
          return 0;
        }
        return 1;
      },
    }),

    autotip({
      isLoading: loading || tableVirtualLoading,
      hideIcon: !loading && !tableVirtualLoading, // loading状态显示图标，其他情况不显示
      emptyText: 'The list has no data yet.',
      style: {
        height: 172,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
      },
    }),
  ];

  // 请求页面整体数据
  useEffect(() => {
    Array.isArray(selectBO) && run({ management_team: selectBO });
  }, [selectBO]);

  useEffect(() => {
    setRecords(orgList?.[RECORDS_MAP?.[segmentValue]] || []);
    // 虚拟loading
    const t = setTimeout(() => {
      setTableVirtualLoading(false);
      clearTimeout(t);
    }, 100);
  }, [segmentValue]);

  // 添加滚动监听及页面打点
  useEffect(() => {
    reportOperationLog(ALL_REPORT_OPERATION_LOG.STUDIO_OVERALL_FINANCE_OVERVIEW);
  }, []);

  return (
    <div className="dbg-studio-overall-finance-overview">
      <div className="dbg-studio-overall-finance-overview-bullet-chart-area">
        <TriggerViewCard
          className="dbg-studio-overall-finance-overview-bullet-chart-item"
          title={
            <div className="dbg-studio-overall-finance-overview-bullet-chart-item__title">
              <span>Gross Revenue ($)</span>
              <span className="dbg-studio-overall-finance-overview-bullet-chart-item__title-date">
                {dateList?.grossRevenue?.ytm_date}
              </span>
            </div>
          }
          loading={loading}
          isNoData={isNoDataOfGrossRevenue}
          tabs={grossRevenueTabs}
          defaultActiveId="download"
        />
        <TriggerViewCard
          className="dbg-studio-overall-finance-overview-bullet-chart-item"
          title={
            <div className="dbg-studio-overall-finance-overview-bullet-chart-item__title">
              <span>Net Profit ($)</span>
              <span className="dbg-studio-overall-finance-overview-bullet-chart-item__title-date">
                {dateList?.netProfit?.ytm_date}
              </span>
            </div>
          }
          loading={loading}
          isNoData={isNoDataOfNetProfit}
          tabs={netProfitTabs}
          defaultActiveId="download"
        />
      </div>
      <Segment
        className="dbg-studio-overall-finance-overview-segment segment-light"
        value={segmentValue}
        options={SEGMENT_OPT}
        onChange={(value: '1' | '2') => {
          setRecords([]);
          setTableVirtualLoading(true); // 虚拟loading
          setSegmentValue(value);
          // 把遮挡边框的dom删除
          // deleteEmptyBorder();
        }}
      />
      <CardSlot className="dbg-studio-overall-finance-overview-list-container">
        <Table
          className={classnames({
            'dbg-common-table dbg-studio-overall-finance-overview-list-table dbg-table-sticky dbh-action-state-center-table':
              true,
            'tbody-bottom-border': loading || tableVirtualLoading || !records?.length,
          })}
          records={
            loading || tableVirtualLoading
              ? []
              : records?.length
              ? records
              : firstLineList
              ? [firstLineList]
              : []
          }
          recordKey="uniqueId"
          columns={getColumns(segmentValue, allTotalDateList) as TableProps['columns']}
          addons={addons}
        />
      </CardSlot>
    </div>
  );
};

export default React.memo(Overview);
